(function (scope) {
  class DtcOfferCart {
    constructor(options = {}) {
      this.fetcher = options.fetcher || ((...args) => fetch(...args));
      const root = options.root || '/';
      this.root = root.endsWith('/') ? root : root + '/';
      this.addUrl = options.addUrl || this.root + 'cart/add.js';
      this.messages = options.messages || {};
      this.signal = options.signal;
      this.uuid = options.uuid || (() => scope.crypto.randomUUID());
    }
    giftMustBeFree() { return true; }
    static pending() {
      try { return scope.DtcOfferPending || scope.sessionStorage?.getItem('dtc-offer-pending') || ''; } catch { return scope.DtcOfferPending || ''; }
    }
    static markPending(group) {
      scope.DtcOfferPending = group;
      try { if (group) scope.sessionStorage?.setItem('dtc-offer-pending', group); else scope.sessionStorage?.removeItem('dtc-offer-pending'); } catch {}
      if (typeof document !== 'undefined') {
        const blocked = Boolean(group);
        (document.querySelectorAll?.('.additional-checkout-buttons, .shopify-payment-button') || []).forEach((node) => node.toggleAttribute('data-dtc-pending-hidden', blocked));
      }
    }
    fail(code) {
      const error = new Error(this.messages[code] || code);
      error.code = code;
      return error;
    }
    async request(url, body, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      const abort = () => controller.abort();
      this.signal?.addEventListener('abort', abort, { once: true });
      if (this.signal?.aborted) controller.abort();
      try {
        const response = await this.fetcher(url, {
          method: body ? 'POST' : 'GET', credentials: 'same-origin', signal: controller.signal,
          headers: { Accept: options.accept || 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
          ...(body ? { body: JSON.stringify(body) } : {})
        });
        if (response.status === 429) throw this.fail('rateLimited');
        let value;
        try { value = await response.json(); } catch { throw this.fail('network'); }
        if (!response.ok || value.status) throw new Error(typeof value.description === 'string' ? value.description : this.messages.network || 'network');
        return value;
      } finally { clearTimeout(timer); this.signal?.removeEventListener('abort', abort); }
    }
    cart() { return this.request(this.root + 'cart.js'); }
    async removeGroup(group) {
      const cart = await this.cart();
      const updates = {};
      cart.items.filter((item) => item.properties?._dtc_offer === group).forEach((item) => { updates[item.key] = 0; });
      return Object.keys(updates).length ? this.request(this.root + 'cart/update.js', { updates }) : cart;
    }
    async preflight(items) {
      const products = new Map();
      for (const item of items) {
        if (!item.id || !Number.isInteger(item.quantity) || item.quantity < 1 || item.available === false) throw this.fail('unavailable');
        if (!item.handle) continue;
        if (!products.has(item.handle)) products.set(item.handle, await this.request(this.root + 'products/' + encodeURIComponent(item.handle) + '.js'));
        const product = products.get(item.handle);
        const variant = product.variants.find((variant) => String(variant.id) === String(item.id));
        if (!variant?.available || product.requires_selling_plan) throw this.fail('unavailable');
        if (item.role === 'main' && typeof item.price === 'number' && variant.price !== item.price) throw this.fail('priceChanged');
      }
    }
    async add(offer, options = {}) {
      const configured = offer.items?.length ? offer.items : [{ id: Number(offer.variantId), quantity: 1, role: 'main', available: offer.available }];
      await this.preflight(configured);
      if (this.signal?.aborted) throw this.fail('network');
      const group = this.uuid();
      const items = configured.map((item) => ({ id: Number(item.id), quantity: item.quantity, properties: {
        _dtc_offer: group, _dtc_role: item.role, _dtc_kit: offer.key,
        _dtc_parent: String(offer.variantId), _dtc_slot: String(item.slot || 0),
        _dtc_ratio: String(item.quantity)
      } }));
      DtcOfferCart.markPending(group);
      try {
        const response = await this.request(this.addUrl, { items, ...(options.sections?.length ? { sections: options.sections, sections_url: options.path || '/' } : {}) });
        const cart = await this.cart();
        const added = cart.items.filter((item) => item.properties?._dtc_offer === group);
        if (added.length !== items.length || items.some((item) => !added.some((line) => String(line.variant_id || line.id) === String(item.id) && String(line.properties?._dtc_slot) === item.properties._dtc_slot && line.quantity === item.quantity))) throw this.fail('unavailable');
        if (cart.items.some((item) => item.properties?._dtc_offer && item.properties._dtc_role === 'gift' && this.giftMustBeFree(item) && item.final_line_price !== 0)) throw this.fail('discount');
        const main = added.find((item) => item.properties?._dtc_role === 'main');
        if (typeof configured[0].price === 'number' && typeof main.original_price === 'number' && main.original_price !== configured[0].price) throw this.fail('priceChanged');
        DtcOfferCart.markPending('');
        return { cart, group, state: { ...response, key: main.key, id: Number(offer.variantId) } };
      } catch (error) {
        try { await new DtcOfferCart({ root: this.root, fetcher: this.fetcher, messages: this.messages }).removeGroup(group); DtcOfferCart.markPending(''); }
        catch { error = this.fail('uncertain'); }
        throw error;
      }
    }
    async sanitize(cart) {
      const limit = cart.items.length + 1;
      for (let pass = 0; pass < limit; pass++) {
        const mains = new Set(cart.items.filter((item) => item.properties?._dtc_role === 'main').map((item) => item.properties._dtc_offer));
        const unsafe = cart.items.filter((item) => item.properties?._dtc_offer && item.properties._dtc_role === 'gift' && (!mains.has(item.properties._dtc_offer) || (this.giftMustBeFree(item) && item.final_line_price !== 0)));
        if (!unsafe.length) return cart;
        const updates = Object.fromEntries(unsafe.map((item) => [item.key, 0]));
        cart = await this.request(this.root + 'cart/update.js', { updates });
      }
      throw this.fail('uncertain');
    }
    async change(key, quantity) {
      if (!Number.isInteger(quantity) || quantity < 0) throw this.fail('unavailable');
      const cart = await this.cart();
      const line = cart.items.find((item) => item.key === key);
      if (!line?.properties?._dtc_offer) throw this.fail('unavailable');
      const updates = { [key]: quantity };
      if (line.properties._dtc_role === 'main') {
        cart.items.filter((item) => item.properties?._dtc_offer === line.properties._dtc_offer && item.properties._dtc_role === 'gift').forEach((item) => {
          updates[item.key] = quantity * Number(item.properties._dtc_ratio || 1);
        });
      }
      return this.sanitize(await this.request(this.root + 'cart/update.js', { updates }));
    }
    async sections(ids, path) {
      const url = new URL(path || this.root, scope.location?.origin || 'https://example.test');
      url.searchParams.set('sections', ids.join(','));
      const sections = await this.request(url.pathname + url.search, undefined, { accept: 'text/html' });
      if (ids.some((id) => typeof sections[id] !== 'string')) throw this.fail('network');
      return sections;
    }
  }
  scope.DtcOfferCart = DtcOfferCart;
  if (typeof module !== 'undefined' && module.exports) module.exports = DtcOfferCart;
  if (typeof document === 'undefined') return;
  const install = () => {
    const config = document.querySelector('[data-dtc-cart-config]');
    if (!config) return;
    const service = new DtcOfferCart({ root: config.dataset.root, messages: config.dataset });
    let current = { items: [] };
    let busy = false;
    const setBusy = (value) => {
      busy = value;
      document.documentElement.toggleAttribute('data-dtc-cart-busy', value);
      document.querySelectorAll('cart-drawer, cart-items').forEach((node) => node.setAttribute('aria-busy', String(value)));
    };
    const notify = (message) => {
      let node = document.querySelector('[data-dtc-cart-message]');
      if (!node) { node = document.createElement('p'); node.setAttribute('data-dtc-cart-message', ''); node.setAttribute('role', 'alert'); document.querySelector('cart-drawer .drawer__inner, cart-items, main')?.prepend(node); }
      if (node) node.textContent = message;
    };
    const remember = (cart) => {
      current = cart;
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', cart.items.length === 0);
      document.querySelectorAll('.additional-checkout-buttons, .shopify-payment-button').forEach((node) => node.toggleAttribute('data-dtc-pending-hidden', Boolean(DtcOfferCart.pending())));
    };
    const refresh = async () => {
      const page = document.querySelector('cart-items');
      const drawer = page ? null : document.querySelector('cart-drawer');
      const parts = drawer ? drawer.getSectionsToRender() : page ? page.getSectionsToRender() : [{ id: 'cart-icon-bubble' }];
      const ids = [...new Set(parts.map((part) => part.section || part.id))];
      const sections = await service.sections(ids, scope.location.pathname);
      if (drawer) drawer.renderContents({ sections });
      else for (const part of parts) {
        const target = document.getElementById(part.id);
        const source = sections[part.section || part.id];
        if (!target || !source) continue;
        const selector = part.selector || '.shopify-section';
        const content = new DOMParser().parseFromString(source, 'text/html').querySelector(selector);
        const inner = target.querySelector(selector) || target;
        if (content) inner.innerHTML = content.innerHTML;
      }
      page?.classList.toggle('is-empty', current.items.length === 0);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', current.items.length === 0);
      const count = document.getElementById('cart-icon-bubble');
      if (count && sections['cart-icon-bubble']) count.innerHTML = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section')?.innerHTML || sections['cart-icon-bubble'];
    };
    const sync = async () => { const pending = DtcOfferCart.pending(); if (pending) { await service.removeGroup(pending); DtcOfferCart.markPending(''); } remember(await service.sanitize(await service.cart())); };
    sync().catch(() => {});
    document.addEventListener('dtc:cart', (event) => remember(event.detail));
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (busy || event.source === 'dtc-offers') return;
      setBusy(true);
      try {
        const before = await service.cart();
        const after = await service.sanitize(before); remember(after);
        if (after.items.length !== before.items.length) await refresh();
      } catch (error) { notify(error.message); }
      finally { setBusy(false); }
    });
    const update = async (event, key, quantity) => {
      const owner = event.target.closest('[data-dtc-key]');
      if (!owner?.dataset.dtcOffer) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (busy) return;
      setBusy(true);
      try {
        remember(await service.change(key, quantity)); await refresh();
        if (document.querySelector('cart-items')) await document.querySelector('cart-drawer-items')?.onCartUpdate?.();
      }
      catch (error) { notify(error.message); }
      finally { setBusy(false); }
    };
    document.addEventListener('click', (event) => {
      const remove = event.target.closest('cart-remove-button');
      if (remove) { const owner = remove.closest('[data-dtc-key]'); if (owner) update(event, owner.dataset.dtcKey, 0); return; }
      const checkout = event.target.closest('[name="checkout"], a[href*="/checkout"], .shopify-payment-button');
      if (checkout && (DtcOfferCart.pending() || current.items.some((item) => item.properties?._dtc_offer))) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (DtcOfferCart.pending()) { notify(config.dataset.uncertain); return; }
        if (busy) return;
        setBusy(true);
        (async () => {
          try {
            const cart = await service.sanitize(await service.cart()); remember(cart);
            if (cart.items.some((item) => item.properties?._dtc_role === 'gift' && service.giftMustBeFree(item) && item.final_line_price !== 0)) throw service.fail('discount');
            scope.location.assign(service.root + 'checkout');
          } catch (error) { notify(error.message); }
          finally { setBusy(false); }
        })();
      }
    }, true);
    document.addEventListener('change', (event) => {
      if (!event.target.matches('input[name="updates[]"]')) return;
      const owner = event.target.closest('[data-dtc-key]');
      if (owner) update(event, owner.dataset.dtcKey, Number(event.target.value));
    }, true);
    document.addEventListener('submit', (event) => {
      if (DtcOfferCart.pending() && (event.submitter?.name === 'checkout' || event.target.action?.includes('/checkout'))) {
        event.preventDefault(); event.stopImmediatePropagation(); notify(config.dataset.uncertain);
      }
    }, true);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install, { once: true });
  else install();
})(typeof window === 'undefined' ? globalThis : window);
